// @flow

import Service from './service';
import StringUtils from './utils/string';
import type { RequestGeoType, AddressType } from '../types/address';
import type { ApiConstructorArgsType } from '../types/api';

const zipRegex = /^\d{5}$/;

export function toAddress(geo: RequestGeoType) {
  const { countyFips, stateCode, ...props } = geo;
  return {
    ...props,
    county_fips: countyFips,
    state: stateCode,
  };
}

export function formatResponseToAddresses(response: Object) {
  if (response && Array.isArray(response.data)) {
    return response.data.map(address => ({
      ...toAddress(address),
      city: StringUtils.capitalize(address.city),
      county: StringUtils.capitalize(address.county),
    }));
  }
  return [];
}

export default class GeoService extends Service {
  constructor(args: ApiConstructorArgsType = {}) {
    super(args);
    this.baseUrl = this.conf.path.searchBase;
  }

  getByZip(zipcode: string):Promise<Array<AddressType>> {
    // geo service returns an empty array with invalid zip supplied, so don't make the request
    if (!zipRegex.test(zipcode)) {
      return Promise.resolve([]);
    }

    return this.api.get(`${this.baseUrl}/geo/counties?zipcode=${zipcode}`)
      .then(res => (
        // Format response
        formatResponseToAddresses(res)
      ));
  }

  getMedicaidAndChipUrls(stateCode: string): Promise<Object> {
    return this.api.get(`${this.conf.path.serviceBase}/content/state/${stateCode}`)
      .then(({ data }) => {
        const {
          ffm,
          medicaidUrl: stateMedicaidUrl,
          name: stateName,
          stateExchange = {},
          medicaidName,
          chipName,
          chipUrl,
        } = data.data;

        const exMedicaidUrl = stateExchange.medicaidUrl;
        // if state has an exchange and a medicaidUrl in the stateEx obj
        if (!ffm && exMedicaidUrl) {
          return {
            medicaidUrl: exMedicaidUrl,
            stateName,
            medicaidName: stateExchange.medicaidName,
            chipName,
            chipUrl,
          };
          // if state is an FFM state and has a medicaidUrl
        } else if (ffm && stateMedicaidUrl) {
          return {
            medicaidUrl: stateMedicaidUrl,
            stateName,
            medicaidName,
            chipName,
            chipUrl,
          };
        }
        return {
          medicaidUrl: 'https://www.medicaid.gov',
          medicaidName: 'Medicaid',
          chipName: 'CHIP',
          chipUrl: 'https://www.healthcare.gov/medicaid-chip/childrens-health-insurance-program/',
        };
      });
  }
}
